import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { telegram } from '../telegram/adapter';

export type AppTheme = 'light' | 'dark';
export const THEME_STORAGE_KEY = 'mandarino.theme';

function isTheme(value: unknown): value is AppTheme {
  return value === 'light' || value === 'dark';
}

export function readExplicitTheme(storage: Storage | undefined = typeof window === 'undefined' ? undefined : window.localStorage): AppTheme | null {
  try {
    const value = storage?.getItem(THEME_STORAGE_KEY);
    return isTheme(value) ? value : null;
  } catch {
    return null;
  }
}

export function resolveInitialTheme(explicit: AppTheme | null, telegramTheme: AppTheme | null): AppTheme {
  return explicit ?? telegramTheme ?? 'light';
}

export function nextTheme(theme: AppTheme): AppTheme {
  return theme === 'light' ? 'dark' : 'light';
}

export function shouldApplyTelegramTheme(explicit: AppTheme | null): boolean {
  return explicit === null;
}

type ThemeContextValue = { theme: AppTheme; toggle: () => void };
const ThemeContext = createContext<ThemeContextValue>({ theme: 'light', toggle: () => undefined });

function browserTelegramTheme(): AppTheme | null {
  try {
    const context = telegram();
    return context.isTelegram ? context.theme : null;
  } catch {
    return null;
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [explicit, setExplicit] = useState<AppTheme | null>(() => readExplicitTheme());
  const [theme, setTheme] = useState<AppTheme>(() => resolveInitialTheme(readExplicitTheme(), browserTelegramTheme()));

  useEffect(() => {
    document.documentElement.dataset.t = theme;
  }, [theme]);

  useEffect(() => {
    let context: ReturnType<typeof telegram> | undefined;
    try {
      context = telegram();
      const applyViewport = () => {
        const current = telegram();
        document.documentElement.style.setProperty('--mandarino-stable-height', `${current.viewportHeight}px`);
      };
      applyViewport();
      const stopTheme = context.onThemeChanged(() => {
        const currentExplicit = readExplicitTheme();
        if (shouldApplyTelegramTheme(currentExplicit)) setTheme(browserTelegramTheme() ?? 'light');
      });
      const stopViewport = context.onViewportChanged(applyViewport);
      return () => {
        stopTheme();
        stopViewport();
      };
    } catch {
      document.documentElement.dataset.t = theme;
    }
  }, []);

  const value = useMemo(() => ({
    theme,
    toggle: () => {
      const next = nextTheme(theme);
      try { window.localStorage.setItem(THEME_STORAGE_KEY, next); } catch { /* preference is best effort */ }
      setExplicit(next);
      setTheme(next);
    },
  }), [theme]);

  void explicit;
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
